import { defineComponent, ref, watch, onMounted, computed, type PropType } from 'vue';
import { Input, Button, message, Empty } from 'ant-design-vue';
import { EditOutlined, DeleteOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons-vue';
import complete from '../../assets/完成编辑.png';
import { Plugin } from '@vue/runtime-dom';
import './index.less';

interface DataSource {
  id?: string;
  key: string;
  value: string | string[]; // 支持单个或多个值
  index?: number;
  isEditing?: boolean;
  editKey?: string;
  editValue?: string | string[]; // 编辑时的临时值
}

/** 属性名/属性值输入框最大字符数 */
const INPUT_MAX_LENGTH = 30;

export const PropertyTable = defineComponent({
  name: 'EditableTable',
  emits: ['change', 'emptyAdd'],
  props: {
    keyTitle: {
      type: String,
      default: '属性名称',
    },
    valueTitle: {
      type: String,
      default: '属性值',
    },
    dataSource: {
      type: Array as PropType<DataSource[]>,
      default: () => [],
    },
    // 属性值是否为多个
    isMultiValue: {
      type: Boolean,
      default: false,
    },
    scrollHeight: {
      type: Number,
      default: 500,
    },
    // 属性名是否可编辑
    keyEditAble: {
      type: Boolean,
      default: true,
    },
    valueAddAble: {
      type: Boolean,
      default: false,
    },
    deleteAble: {
      type: Boolean,
      default: true,
    },
    showValue: {
      type: Boolean,
      default: true,
    },
    showIndex: {
      type: Boolean,
      default: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    /** 为 true 时表格始终显示为编辑态（输入框），保存后仍保持可编辑 */
    alwaysEditing: {
      type: Boolean,
      default: false,
    },
    /** 无数据时是否在「暂无数据」后展示可点击的链接（点击触发 emptyAdd） */
    emptyAddable: {
      type: Boolean,
      default: false,
    },
    emptyAddLinkText: {
      type: String,
      default: '新增属性',
    },
  },
  setup(props, { emit }) {
    const dataSource = ref<DataSource[]>([]);

    const columns = computed(() => [
      {
        title: props.showIndex ? '序号' : '',
        dataIndex: 'index',
        key: 'index',
        width: props.showIndex ? '10%' : '0',
        align: 'center',
        customRender: ({ index }) => {
          const number = index + 1;
          if (!props.showIndex) {
            return null;
          }
          return number < 10 ? `0${number}` : number.toString();
        },
      },
      {
        title: props.keyTitle,
        dataIndex: 'editKey',
        width: props.showValue ? '30%' : '40%',
        ellipsis: true,
        customRender: ({ record }: any) => {
          const editing = !!record.isEditing || props.alwaysEditing;
          return editing && props.keyEditAble ? (
            <div>
              <Input
                placeholder="请输入"
                showCount
                maxlength={INPUT_MAX_LENGTH}
                value={record.editKey}
                onInput={(e) => {
                  record.editKey = e.target.value;
                }}
                onBlur={() => {
                  if (props.alwaysEditing) {
                    onComplete(record, true);
                  }
                }}
              />
            </div>
          ) : (
            record.editKey
          );
        },
      },
      {
        title: props.showValue ? props.valueTitle : '',
        dataIndex: 'editValue',
        width: props.showValue ? '50%' : '0',
        customRender: ({ record }: any) => {
          const editing = !!record.isEditing || props.alwaysEditing;
          return editing ? (
            props.isMultiValue ? (
              Array.isArray(record.editValue) ? (
                <div>
                  {record.editValue.map((value, index) => (
                    <div class={'value-row'}>
                      <Input
                        key={index}
                        placeholder="请输入"
                        showCount
                        maxlength={INPUT_MAX_LENGTH}
                        value={value}
                        style={{ marginBottom: record.editValue.length > 1 ? '8px' : 'unset' }}
                        onInput={(e) => {
                          if (Array.isArray(record.editValue)) {
                            record.editValue[index] = e.target.value;
                          }
                        }}
                        onBlur={() => {
                          if (props.alwaysEditing) {
                            onComplete(record, true);
                          }
                        }}
                      />
                    </div>
                  ))}
                  {props.valueAddAble && (
                    <Button
                      type="dashed"
                      size="small"
                      icon={<PlusOutlined />}
                      style={{ marginTop: '8px' }}
                      onClick={() => onAddValue(record)}
                    >
                      添加值
                    </Button>
                  )}
                </div>
              ) : null
            ) : (
              <Input
                placeholder="请输入"
                showCount
                maxlength={INPUT_MAX_LENGTH}
                value={typeof record.editValue === 'string' ? record.editValue : ''}
                onInput={(e) => {
                  record.editValue = e.target.value;
                }}
                onBlur={() => {
                  if (props.alwaysEditing) {
                    onComplete(record, true);
                  }
                }}
              />
            )
          ) : props.isMultiValue ? (
            Array.isArray(record.value) ? (
              <div class="multi-value">
                {record.value.map((value, index) => (
                  <div key={index} class="value-line">
                    {value}
                  </div>
                ))}
              </div>
            ) : (
              <div>{record.value}</div>
            )
          ) : (
            <div>{record.value}</div>
          );
        },
      },
      ...(props.readOnly || (props.alwaysEditing && !props.deleteAble)
        ? []
        : [
            {
              title: '操作',
              key: 'action',
              width: props.alwaysEditing ? '6%' : '10%',
              customRender: ({ record }: any) => {
                if (props.alwaysEditing) {
                  return (
                    <div class="icons-row">
                      {props.deleteAble && (
                        <DeleteOutlined class="icon" onClick={() => onDelete(record)} />
                      )}
                    </div>
                  );
                }
                const editing = !!record.isEditing || props.alwaysEditing;
                return editing ? (
                  <div class="icons-row">
                    <img
                      src={complete}
                      class="icon"
                      onClick={() => onComplete(record)}
                      style={{ color: 'green' }}
                    />
                    <CloseOutlined class="icon" onClick={() => onQuit(record)} />
                  </div>
                ) : (
                  <div class="icons-row">
                    <EditOutlined
                      class="icon"
                      onClick={() => onEdit(record)}
                      style={{ marginRight: '8px' }}
                    />
                    {props.deleteAble && (
                      <DeleteOutlined class="icon" onClick={() => onDelete(record)} />
                    )}
                  </div>
                );
              },
            },
          ]),
    ]);

    const mapFromProps = (list: DataSource[]) =>
      list.map((it) => ({
        ...it,
        editKey: it.key,
        editValue: Array.isArray(it.value) ? [...it.value] : it.value,
        isEditing: props.alwaysEditing ? true : it.isEditing,
      }));

    onMounted(() => {
      if (props.dataSource.length) {
        dataSource.value = mapFromProps(props.dataSource as DataSource[]);
      } else {
        dataSource.value = [];
      }
    });

    watch(
      () => props.dataSource,
      (val) => {
        if (val.length) {
          dataSource.value = mapFromProps([...val] as DataSource[]);
        } else {
          dataSource.value = [];
        }
      }
    );

    watch(
      () => props.alwaysEditing,
      (on) => {
        dataSource.value.forEach((row) => {
          row.isEditing = !!on;
        });
      }
    );

    const onEdit = (record: any) => {
      record.isEditing = true;
    };

    const onQuit = (record: DataSource) => {
      record.editKey = record.key;
      record.editValue = Array.isArray(record.value) ? [...record.value] : record.value;
      if (!props.alwaysEditing) {
        record.isEditing = false;
      }
    };

    const onComplete = (record: DataSource, silent = false) => {
      if (
        record.key === record.editKey &&
        JSON.stringify(record.value) === JSON.stringify(record.editValue)
      ) {
        if (!props.alwaysEditing) {
          record.isEditing = false;
        }
        return;
      }
      if (!record.editKey) {
        message.error('属性名称不能为空');
        return;
      }
      if (dataSource.value.find((item) => item.key === record.editKey && item.id !== record.id)) {
        message.error('属性名称已存在');
        return;
      } else {
        record.key = record.editKey;
        record.value = props.isMultiValue
          ? Array.isArray(record.editValue)
            ? [...record.editValue]
            : [record.editValue]
          : typeof record.editValue === 'string'
          ? record.editValue
          : ('' as any);
        if (!props.alwaysEditing) {
          record.isEditing = false;
        }

        const freezeData = dataSource.value.map(({ isEditing, ...rest }) => rest);
        emit('change', {
          type: 'update',
          record,
          records: freezeData,
        });
        if (!silent) {
          message.success('保存成功');
        }
      }
    };

    const onDelete = (record: DataSource) => {
      dataSource.value = dataSource.value.filter((item) => item.key !== record.key);
      const freezeData = dataSource.value.map(({ isEditing, ...rest }) => rest);
      emit('change', {
        type: 'delete',
        record,
        records: freezeData,
      });
      message.success('删除成功');
    };

    const onAddValue = (record: DataSource) => {
      if (Array.isArray(record.editValue)) {
        record.editValue.push(''); // 添加一个空值
      }
    };

    return {
      dataSource,
      columns,
    };
  },
  render() {
    const p = this.$props;
    const locale = p.emptyAddable
      ? {
          emptyText: (
            <div class="d-edit-table__empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    暂无数据
                    <a
                      class="d-edit-table__empty-link"
                      onClick={(e: Event) => {
                        e.preventDefault();
                        this.$emit('emptyAdd');
                      }}
                    >
                      {p.emptyAddLinkText}
                    </a>
                  </span>
                }
              />
            </div>
          ),
        }
      : undefined;
    return (
      <a-table
        class="d-edit-table"
        dataSource={this.dataSource}
        columns={this.columns}
        rowKey="key"
        pagination={false}
        size="middle"
        scroll={{ y: p.scrollHeight }}
        locale={locale}
        style={{ minHeight: p.scrollHeight + 'px' }}
      />
    );
  },
});

export const DPropertyTable: Plugin = {
  install: (app) => {
    // Plugin code goes here
    app.component('DPropertyTable', PropertyTable);
  },
};
